import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Container,
  Grid2,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import {
  determineHeaderTimes,
  generateTimeSlotOptions,
  hexToRgba,
  notifErr,
  notifOk,
} from "../../components/Functions";
import handleAxiosError from "../../components/ErrorHandler";
import { Loading } from "../../components/Loading";
import { TrainingArea } from "../../model/entity/TrainingArea";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  ChangeCircle,
  EventBusy,
  EventBusyOutlined,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  ReportGmailerrorred,
} from "@mui/icons-material";
import { CourseInstance } from "../../model/entity/CourseInstance";
import { Coach } from "../../model/entity/Coach";
import { getFetcher, putFetcher } from "../../components/Fetchers";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import SubstitutionDialog from "../../components/SubstitutionDialog";

const Substitutions = () => {
  const navigate = useNavigate();
  const [headerTimes, setHeaderTimes] = useState<string[] | null>(null);
  const [instances, setInstances] = useState<CourseInstance[]>([]);
  const [areas, setAreas] = useState<TrainingArea[] | null>(null);
  const [coaches, setCoaches] = useState<Coach[] | null>(null);
  const [selectedInstance, setSelectedInstance] =
    useState<CourseInstance | null>(null);
  const [sameCourseInstances, setSameCourseInstances] = useState<
    CourseInstance[] | null
  >(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [openSubstitutionDialog, setOpenSubstitutionDialog] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  const [hoveredInstanceId, setHoveredInstanceId] = useState<number | null>(
    null
  );
  const [cancelMode, setCancelMode] = useState(false);
  const [canceledInstanceIds, setCanceledInstanceIds] = useState<number[]>([]);

  const handleHoveredInstanceId = (slotId: number | null) => {
    if (slotId !== hoveredInstanceId) setHoveredInstanceId(slotId);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const areasRes = await getFetcher(
          `${process.env.REACT_APP_API_URL}/training-areas/sorted-by-code`
        );
        setAreas(areasRes.data);

        const coachesRes = await getFetcher(
          `${process.env.REACT_APP_API_URL}/coaches`
        );
        setCoaches(coachesRes.data);
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedDate || openSubstitutionDialog) return;
    setCancelMode(false);

    const fetchData = async () => {
      try {
        const config = { date: selectedDate.format("YYYY-MM-DD") };
        const instancesRes = await getFetcher(
          `${process.env.REACT_APP_API_URL}/course-instances/all-active-by-date`,
          config
        );
        setInstances(instancesRes.data);
        const { from, to } = determineHeaderTimes(instancesRes.data);
        setHeaderTimes(generateTimeSlotOptions(from, to, to !== 24));
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };
    fetchData();
  }, [selectedDate, openSubstitutionDialog]);

  const handleShiftDay = (days: number) => {
    if (!selectedDate) return;
    const newSelectedDate = selectedDate.add(days, "d");
    setSelectedDate(newSelectedDate);
  };

  const handleSelectedInstanceCancelModeDisabled = (
    instance: CourseInstance
  ) => {
    const fetchData = async () => {
      try {
        const config = { courseId: instance.course.id };
        const instancesRes = await getFetcher(
          `${process.env.REACT_APP_API_URL}/course-instances/all-by-course`,
          config
        );
        setSelectedInstance(instance);
        setSameCourseInstances(instancesRes.data);
        setOpenSubstitutionDialog(true);
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };
    fetchData();
  };

  const handleSelectedInstanceCancelModeEnabled = (
    instance: CourseInstance
  ) => {
    let updatedIds: number[];
    let updatedInstances: CourseInstance[];
    const updatedInstance: CourseInstance = {
      ...instance,
      canceled: !instance.canceled,
    };

    updatedIds = canceledInstanceIds.includes(instance.id)
      ? canceledInstanceIds.filter((id) => id !== instance.id)
      : [...canceledInstanceIds, instance.id];

    updatedInstances = instances.map((i) =>
      i.id === instance.id ? updatedInstance : i
    );

    setCanceledInstanceIds(updatedIds);
    setInstances(updatedInstances);
  };

  const handleChangeCancelMode = () => {
    if (cancelMode && canceledInstanceIds.length > 0)
      setOpenConfirmationDialog(true);
    setCancelMode(!cancelMode);
  };

  const handleCancelModeSubmit = async () => {
    try {
      await putFetcher(
        `${process.env.REACT_APP_API_URL}/course-instances`,
        instances
      );
      notifOk("Změny byly uloženy.");
      setOpenConfirmationDialog(false);
      setCanceledInstanceIds([]);
    } catch (err) {
      notifErr(handleAxiosError(err));
    }
  };

  const handleCancelModeCancel = () => {
    const updatedArr = instances.map((i) =>
      canceledInstanceIds.includes(i.id) ? { ...i, canceled: !i.canceled } : i
    );

    setInstances(updatedArr);
    setCanceledInstanceIds([]);
  };

  const handleSubstitutionDialogCancel = () => {
    setOpenSubstitutionDialog(false);
    setTimeout(() => {
      setSelectedInstance(null);
      setSameCourseInstances(null);
    }, 100);
  };

  const findInstanceForCell = (area: TrainingArea, time: string) => {
    const matchingCourse = instances.find((instance) => {
      const instanceStart = instance.startTime.substring(0, 5); // the times came in format "HH:MM"
      const instanceEnd = instance.endTime.substring(0, 5);

      const isWithinTime =
        time >= instanceStart &&
        (time < instanceEnd || instanceEnd === "00:00");
      const isAtThisArea = instance.trainingAreaIds.includes(area.id!);

      return isWithinTime && isAtThisArea;
    });

    return matchingCourse;
  };

  if (!instances || !areas || !coaches || !headerTimes) return <Loading />;

  const getCellSpan = (instance: CourseInstance) => {
    const startTimeIndex = headerTimes.indexOf(
      instance.startTime.substring(0, 5)
    );
    const endTime = instance.endTime.substring(0, 5);
    const endTimeIndex =
      endTime === "00:00"
        ? headerTimes.length
        : headerTimes.indexOf(instance.endTime.substring(0, 5));
    return endTimeIndex - startTimeIndex;
  };

  const confirmationDialogMsg = <div>Chcete uložit změny?</div>;

  return (
    <Container
      className="scheduleContainer"
      maxWidth={false}
      sx={{ userSelect: "none" }}
    >
      <ConfirmationDialog
        open={openConfirmationDialog}
        setOpen={setOpenConfirmationDialog}
        msg={confirmationDialogMsg}
        handleSubmit={handleCancelModeSubmit}
        handleCancel={handleCancelModeCancel}
      />

      {selectedInstance && sameCourseInstances && (
        <SubstitutionDialog
          open={openSubstitutionDialog}
          selectedInstance={selectedInstance!}
          courseInstances={sameCourseInstances!}
          coaches={coaches}
          handleCancel={handleSubstitutionDialogCancel}
        />
      )}
      <TableContainer
        className="scheduleTableContainer"
        onMouseLeave={() => handleHoveredInstanceId(null)}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: 0.5,
            position: "sticky",
            left: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "fit-content",
              display: "flex",
              alignItems: "center",
              border: "1px solid var(--gradientEnd)",
              borderRadius: "2rem",
            }}
          >
            <IconButton
              sx={{ color: "var(--gradientEnd)" }}
              onClick={() => handleShiftDay(-7)}
            >
              <KeyboardDoubleArrowLeft />
            </IconButton>
            <IconButton
              sx={{ color: "var(--gradientEnd)" }}
              onClick={() => handleShiftDay(-1)}
            >
              <KeyboardArrowLeft />
            </IconButton>
            <Typography
              id={"dateLabel"}
              variant="h5"
              sx={{
                cursor: "pointer",
                color: "var(--gradientEnd)",
                mx: ".5rem",
                fontSize: "1.3rem",
              }}
              onClick={() => setOpenDatePicker(true)}
            >
              {selectedDate?.format("DD.MM.YYYY")}
            </Typography>
            <IconButton
              sx={{ color: "var(--gradientEnd)" }}
              onClick={() => handleShiftDay(1)}
            >
              <KeyboardArrowRight />
            </IconButton>
            <IconButton
              sx={{ color: "var(--gradientEnd)" }}
              onClick={() => handleShiftDay(7)}
            >
              <KeyboardDoubleArrowRight />
            </IconButton>
          </Paper>
        </Box>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            open={openDatePicker}
            value={selectedDate}
            onClose={() => setOpenDatePicker(false)}
            onChange={(newValue) => setSelectedDate(newValue)}
            slotProps={{
              textField: {
                sx: {
                  width: 0,
                  height: 0,
                  opacity: 0,
                  overflow: "hidden",
                  position: "absolute",
                },
                inputRef: (input) => {
                  if (input && openDatePicker) {
                    input.focus();
                  }
                },
              },
              popper: {
                anchorEl: document.getElementById("dateLabel"),
                placement: "bottom",
              },
            }}
          />
        </LocalizationProvider>
        <Table sx={{ mt: ".75rem" }}>
          <TableHead>
            <TableRow
              key="timeHeader"
              onMouseEnter={() => handleHoveredInstanceId(null)}
            >
              <TableCell
                key={"cancel-mode-cell"}
                className="scheduleHeaderTimeCell cancelModeCell"
                onClick={handleChangeCancelMode}
                sx={{
                  backgroundColor: cancelMode
                    ? "var(--redNotOKHover) !important"
                    : "var(--scheduleBorderCellGray) !important",
                }}
              >
                <Tooltip
                  key="edit-mode-tooltip"
                  title={
                    (cancelMode ? "Vypnout" : "Zapnout") + " mód rušení lekcí"
                  }
                >
                  <span>
                    {cancelMode ? (
                      <EventBusy sx={{ color: "var(--redNotOK)" }} />
                    ) : (
                      <EventBusyOutlined sx={{ color: "var(--redNotOK)" }} />
                    )}
                  </span>
                </Tooltip>
              </TableCell>
              {headerTimes.map((time, idx) => (
                <TableCell key={`${time}`} className="scheduleHeaderTimeCell">
                  {time}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {areas.map((area, areaIndex) => (
              <TableRow key={areaIndex}>
                <TableCell
                  key={area.id}
                  className="scheduleTrainingAreaCell"
                  onClick={() =>
                    navigate(`/schedule/training-areas/edit/${area.id}`)
                  }
                  onMouseEnter={() => handleHoveredInstanceId(null)}
                >
                  {area.code}
                </TableCell>
                {headerTimes.map((time, timeIndex) => {
                  const instance = findInstanceForCell(area, time);
                  const cellKey = `${area.id}-${time}`;
                  if (timeIndex > 0) {
                    const prevTime = headerTimes[timeIndex - 1];
                    const prevInstance = findInstanceForCell(area, prevTime);
                    if (prevInstance && prevInstance.id === instance?.id) {
                      return null;
                    }
                  }

                  if (instance) {
                    const span = getCellSpan(instance);
                    const canceled = instance.canceled;
                    const canceledOp = 0.4;

                    let above = false;
                    let below = false;
                    let objectAbove =
                      areaIndex !== 0
                        ? findInstanceForCell(areas[areaIndex - 1], time)
                        : null;
                    let objectBelow =
                      areaIndex + 1 !== areas.length
                        ? findInstanceForCell(areas[areaIndex + 1], time)
                        : null;
                    if (objectAbove) above = objectAbove.id === instance.id;
                    if (objectBelow) below = objectBelow.id === instance.id;

                    const isTop = !above && below;
                    const isBottom = above && !below;
                    const isOnlyOne = !above && !below;

                    return (
                      <TableCell
                        key={cellKey}
                        className="scheduleCourseCell"
                        colSpan={span}
                        sx={{
                          borderTop:
                            isTop || isOnlyOne ? "1px solid white" : "none",
                          borderBottom:
                            isBottom || isOnlyOne ? "1px solid white" : "none",
                          borderLeft: "1px solid white",
                          borderRight: "1px solid white",
                          opacity: hoveredInstanceId === instance.id ? 0.7 : 1,
                          background: canceled
                            ? `${hexToRgba(instance.course.color, canceledOp)}`
                            : instance.course.color,
                        }}
                        onClick={() => {
                          cancelMode
                            ? handleSelectedInstanceCancelModeEnabled(instance)
                            : handleSelectedInstanceCancelModeDisabled(
                                instance
                              );
                        }}
                        onMouseMove={() => handleHoveredInstanceId(instance.id)}
                      >
                        <Box className="scheduleCourseContainer">
                          {!above && (
                            <Typography
                              className="courseContainerText"
                              variant="caption"
                              sx={{
                                opacity: canceled ? canceledOp : 1,
                              }}
                            >
                              <b>{instance.course.name}</b> (
                              {instance.course.memberInCourses.length}/
                              {instance.course.capacity})
                            </Typography>
                          )}
                          {instance.canceled && (isTop || isOnlyOne) && (
                            <Typography className="scheduleCanceledCellLabel">
                              Zrušeno
                            </Typography>
                          )}
                          {!below && (
                            <Grid2
                              sx={{
                                opacity: canceled ? canceledOp : 1,
                                marginTop: "auto",
                              }}
                            >
                              {instance.course.coachLeadsCourses.length > 0 ? (
                                instance.course.coachLeadsCourses.map(
                                  (c, index) => (
                                    <Typography
                                      key={c.coach.id}
                                      className="courseContainerText"
                                      variant="caption"
                                    >
                                      {c.coach.nickname}
                                      {index !==
                                      instance.course.coachLeadsCourses.length -
                                        1
                                        ? ", "
                                        : ""}
                                    </Typography>
                                  )
                                )
                              ) : (
                                <Typography className="courseContainerText" variant="caption">
                                  bez trenéra
                                </Typography>
                              )}
                              {instance.coachIds.length === 0 &&
                              instance.course.coachLeadsCourses.length !== 0 ? (
                                <ReportGmailerrorred
                                  sx={{
                                    color: "var(--redNotOK)",
                                    fontSize: "1.1rem",
                                    ml: "0.1rem",
                                  }}
                                />
                              ) : (
                                (instance.coachIds.length !==
                                  instance.course.coachLeadsCourses.length ||
                                  instance.coachIds.some((id) =>
                                    instance.course.coachLeadsCourses.every(
                                      (clc) => clc.coach.id !== id
                                    )
                                  )) && (
                                  <ChangeCircle
                                    sx={{
                                      color: "var(--gradientStart)",
                                      fontSize: "1.1rem",
                                      ml: "0.1rem",
                                    }}
                                  />
                                )
                              )}
                            </Grid2>
                          )}
                        </Box>
                      </TableCell>
                    );
                  }

                  return (
                    <TableCell
                      key={cellKey}
                      className="scheduleEmptyCell"
                      onMouseEnter={() => handleHoveredInstanceId(null)}
                    />
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default Substitutions;
